import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  DialogActions,
  Button,
  Typography,
  Paper,
  Link,
} from "@mui/material";

import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import { FilePondFile } from "filepond";
import { useSnackbar } from "notistack";
import { CloseButton } from "../../../../components/buttons/CloseButton";
import { ProcessButton } from "../../../../components/buttons/ProcessButton";
import {
  CsvResult,
  CsvAnalysys,
  CsvImportOptions,
  CsvJobId,
  APPROVE_SECTION_ID,
  countNotApproved,
  syncMeta,
} from "../../../../features/import/csv";
import { useImportJob } from "../../../../features/import/import.service";
import { useProducts } from "../../../../features/products/product-func";
import { useDbOperations } from "../../../data-func";
import { CategorySelect } from "../../categories/CategorySelect";
import { CollectionSelect } from "../../collections/CollectionSelect";
import {
  ClearedValuesNotice,
  ImportCsvAnalysis,
} from "./import-csv-analysis";
import { ImportCsvPreview } from "./import-csv-preview";
import { StoreExportNotice } from "./import-csv-essentials";
import { ImportStatusLine, ImportProgress } from "./import-progress";
import { uploadFile } from "../../../../features/data/file-util";
import { logError } from "../../../../services/logging";
import {
  stashPendingFile,
  takePendingFile,
} from "../../../../features/import/pendingFile";
import { journeyManager } from "../../../../features/guide/journeyManager";
import { appFeatures } from "../../../../features/guide/features";
import HelpButton from "../../../../components/buttons/HelpButton";
import { Link as RouterLink } from "react-router-dom";
import { IconTypes } from "../../../../components/icons.types";

registerPlugin(FilePondPluginFileValidateType);

interface ImportCsvModalProps {
  categoryId?: string;
  collectionId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSample: () => void;
  onImport?: () => void;
}

export const ImportCsvModal = (props: ImportCsvModalProps) => {
  if (!props?.isOpen) {
    return null;
  }
  return <ImportCsvModalInner {...props} />;
};

type ImportStep = "analyze" | "preview" | "import" | "done" | "failed";

const XLSX_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const XLS_TYPE = "application/vnd.ms-excel";
const ACCEPTED_TYPES = ["text/csv", "text/plain", XLSX_TYPE, XLS_TYPE];

/**
 * The type FilePond validates, taken from the extension when it is one we know. The browser's
 * own answer depends on what is installed: Windows reports a .csv as `application/vnd.ms-excel`
 * when Excel is present, and an .xlsx as an empty string when it is not.
 */
const detectImportFileType = (source: File, type: string): Promise<string> => {
  const name = source?.name?.toLowerCase() ?? "";
  if (name.endsWith(".csv")) return Promise.resolve("text/csv");
  if (name.endsWith(".xlsx")) return Promise.resolve(XLSX_TYPE);
  if (name.endsWith(".xls")) return Promise.resolve(XLS_TYPE);
  return Promise.resolve(type);
};

/**
 * The choices an analysis depends on: where uncategorized products go, the code, name, price,
 * photo and description columns, and whether a store's export goes on as a file.
 */
const choiceKey = (o: CsvImportOptions) => {
  const mode = o.categoryMode ?? "existing";
  const category =
    mode === "new"
      ? (o.newCategory ?? "").trim()
      : mode === "column"
        ? (o.categoryColumn ?? "") + ">" + (o.fallbackCategoryId ?? "")
        : (o.categoryId ?? "");
  return [
    mode,
    category,
    o.codeField ?? "",
    o.nameField ?? "",
    o.priceField ?? "",
    o.imageField ?? "",
    o.descriptionField ?? "",
    o.keepStoreFile ? "file" : "",
  ].join("|");
};

const scrollToApprove = () =>
  document
    .getElementById(APPROVE_SECTION_ID)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });

/** Where checkout brings the user back: the product list, with this modal already open. */
const CSV_RETURN_URL = "/products?import=csv";

const ImportCsvModalInner = ({
  isOpen,
  onClose,
  onSample,
  onImport,
  collectionId,
  categoryId,
}: ImportCsvModalProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const { categoryNames } = useProducts();
  const { processDb } = useDbOperations();

  const [csvFile, setCsvFile] = useState<FilePondFile>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<CsvResult>();

  // The CSV that a plan wall interrupted. The wall keeps it (`keepCsvForReturn`) as the user
  // leaves for checkout; when checkout returns them to `CSV_RETURN_URL` this modal opens with
  // the same file already chosen. Traced users re-uploaded the same CSV two and four times
  // after paying, and one took 30 minutes to get back to the import (S34 § 8, yrmc, Kootenay).
  //
  // The file goes in through the pond instance, not the `files` prop: react-filepond
  // skips the render that follows its own file event, so a prop set at the wrong
  // moment is silently never applied.
  const pondRef = useRef<FilePond>(null);
  const [restored, setRestored] = useState(false);
  useEffect(() => {
    let live = true;
    takePendingFile("csv-import").then((file) => {
      if (!live || !file || !pondRef.current) return;
      setRestored(true);
      void pondRef.current.addFile(file);
    });
    return () => {
      live = false;
    };
  }, []);
  const keepCsvForReturn = useCallback(() => {
    if (csvFile?.file) void stashPendingFile("csv-import", csvFile.file);
  }, [csvFile]);

  const [step, setStep] = useState<ImportStep>();

  // The first analysis of the current file, kept so Back can return to it from the preview.
  const [firstAnalysis, setFirstAnalysis] = useState<CsvResult>();
  // The field, category and collection choices the preview was built from. The preview writes
  // nothing: the choices travel with its request, Back returns to them, and Import saves them.
  const [chosenAnalysis, setChosenAnalysis] = useState<CsvResult>();
  // The column and category choices the analysis on screen was made with. When the user changes
  // one, that analysis is stale and the next click reads the file again (see `process`).
  const [analyzedKey, setAnalyzedKey] = useState<string>();
  const [firstKey, setFirstKey] = useState<string>();

  const initialOptions: CsvImportOptions = useMemo(() => {
    return {
      collectionId: collectionId ?? "",
      categoryId: categoryId ?? "",
    };
  }, [categoryId, collectionId]);

  const [options, setOptions] = useState<CsvImportOptions>({
    ...initialOptions,
  });

  const { startJob, jobStatus, resetJob, lastJobStatus } = useImportJob({
    serviceJobId: CsvJobId,
    onFinish: () => {
      setUploading(false);
      setStep("done");
      onImport?.();
      enqueueSnackbar("Imported " + csvFile?.filename, {
        variant: "success",
      });
    },
    onContinue: () => {
      setStep("import");
      setUploading(true);
    },
  });

  const upload = useCallback(
    async (allOptions: CsvImportOptions, accepted?: CsvAnalysys) => {
      // Only the chosen way of placing uncategorized products is sent; the other two keep what
      // the user entered, so switching back does not lose it.
      const mode = allOptions.categoryMode ?? "existing";
      const uploadOptions: CsvImportOptions = {
        ...allOptions,
        categoryId:
          mode === "existing"
            ? allOptions.categoryId
            : mode === "column"
              ? (allOptions.fallbackCategoryId ?? "")
              : "",
        fallbackCategoryId: "",
        ignoreEmptyCells: allOptions.ignoreEmptyCells ?? true,
        newCategory: mode === "new" ? (allOptions.newCategory ?? "") : "",
        categoryColumn:
          mode === "column" ? (allOptions.categoryColumn ?? "") : "",
      };
      setUploading(true);
      setOptions(allOptions);

      // CM2-2QF: uploadFile rejects when the request never completes. Nothing caught it here,
      // so the modal kept its "uploading" state forever with no message.
      const onUploadError = (e: unknown) => {
        logError(e, "csv upload");
        setUploading(false);
        enqueueSnackbar(
          e instanceof Error
            ? e.message
            : "Upload failed. Check your connection and try again.",
          { variant: "error" },
        );
      };

      await uploadFile(
        "csv-import",
        csvFile.file,
        csvFile.filename,
        // Kept out of `options`: the choices belong to this one preview request.
        accepted
          ? {
              ...uploadOptions,
              accepted: JSON.stringify({ categories: accepted.categories }),
            }
          : uploadOptions,
        async (request) => {
          const r = JSON.parse(request.response) as CsvResult;
          setResult(r);
          const needResolution =
            !r?.analysis?.resolved &&
            !r?.records?.length &&
            uploadOptions.importMode !== "direct";
          if (needResolution) {
            setStep("analyze");
            setFirstAnalysis((first) => first ?? r);
            setAnalyzedKey(choiceKey(allOptions));
            // After a re-read the choices at the top are settled; what is left is below them.
            if (allOptions.importMode === "analysis" && r.analysis?.categoryChanged) {
              setTimeout(scrollToApprove, 150);
            }
            setFirstKey((first) => first ?? choiceKey(allOptions));
            if (r.analysis?.missedCategories > 0 && categoryNames.length) {
              setOptions({
                ...allOptions,
                categoryId: allOptions.categoryId || categoryNames[0].value,
              });
            }
          } else if (r.records?.length > 0 && step !== "import") {
            setStep("preview");
          }
          if (r.jobId) {
            setStep("import");
            startJob(r.jobId, r.fileName, r.count);
          } else {
            setUploading(false);
          }
          journeyManager.updateJourneyProgress(
            "product_setup",
            "import_preparation",
          );
        },
      ).catch(onUploadError);
    },
    [categoryNames, csvFile, startJob, step, enqueueSnackbar],
  );

  // The only place the account is written: the chosen fields, categories and collections are
  // saved, then the import starts.
  const reImport = useCallback(
    async (analysis: CsvAnalysys) => {
      processDb(
        async () => {
          return await syncMeta(analysis);
        },
        async () => {
          await upload({ ...options, importMode: "direct", preview: false });
        },
        setUploading,
        null,
        "Error uploading the file.",
        "products",
      );
    },
    [options, processDb, upload],
  );

  // What the user picked for products with no category, in the mode that is selected.
  const categoryMode = options.categoryMode ?? "existing";
  const categoryChosen =
    categoryMode === "new"
      ? !!options.newCategory?.trim()
      : categoryMode === "column"
        ? !!options.categoryColumn
        : !!options.categoryId;
  // A new category needs its name and a category column needs picking before going on.
  const categoryBlocked =
    step === "analyze" &&
    result?.analysis?.missedCategories > 0 &&
    categoryMode !== "existing" &&
    !categoryChosen;
  // The analysis on screen was made with other choices than the ones selected now.
  const stale = step === "analyze" && analyzedKey !== choiceKey(options);
  const notApproved =
    step === "analyze" && !stale ? countNotApproved(result) : 0;

  const process = useCallback(
    async (isPreview?: boolean) => {
      if (stale) {
        // A category, or a code or name column, was picked on this screen. The server finds a
        // file's new fields per category, so the pass that produced this screen does not show
        // what the choice leads to - importing now would drop every column the category does
        // not already have, without a word. Analyze again with the choices, as a preview so
        // that a file with nothing left to resolve stops at the preview instead of importing.
        upload({ ...options, importMode: "analysis", preview: true });
      } else if (step === "analyze" && options.preview) {
        setChosenAnalysis(result);
        upload(
          { ...options, importMode: "direct", preview: true },
          result.analysis,
        );
      } else if (step === "analyze") {
        await reImport(result.analysis);
      } else if (step === "preview" && chosenAnalysis) {
        await reImport(chosenAnalysis.analysis);
      } else {
        const opts = {
          ...options,
          preview: isPreview || (step !== "preview" && !!options.preview),
        };
        upload(opts);
      }
    },
    [
      options,
      reImport,
      step,
      upload,
      result,
      stale,
      chosenAnalysis,
    ],
  );

  // Back is two steps: preview -> the field choices it was built from -> the first screen
  // (code, name and category columns).
  const backToAnalysis = useCallback(() => {
    if (step === "preview" && chosenAnalysis) {
      setResult(chosenAnalysis);
    } else {
      setResult(firstAnalysis);
      setChosenAnalysis(null);
      setAnalyzedKey(firstKey);
    }
    setStep("analyze");
  }, [firstAnalysis, firstKey, chosenAnalysis, step]);

  // S23: block committing an anonymous-catalog import until the user picks a
  // name column or explicitly confirms using the code as the name. The server
  // forces the analyze step for this case (analysis.nameUnresolved); this gate
  // stops the "Update & Import" click that would otherwise write 1,2,3 names.
  const nameBlocked =
    step === "analyze" &&
    !!result?.analysis?.nameUnresolved &&
    !options.nameField &&
    !options.useCodeAsName;

  // B416: a store's export waits for the user to choose the store import or the file.
  const storeBlocked =
    step === "analyze" &&
    !!result?.analysis?.storeExportPending &&
    !options.keepStoreFile;

  const resetImport = useCallback(() => {
    setCsvFile(null);
    setResult(null);
    setStep(null);
    setFirstAnalysis(null);
    setChosenAnalysis(null);
    setAnalyzedKey(null);
    setFirstKey(null);
    setOptions({ ...initialOptions });
    resetJob();
  }, [initialOptions, resetJob]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      // The floating AI Help button sits above dialogs at bottom-right; keep
      // the action row clear of it (B354).
      PaperProps={{ sx: { mb: 11, maxHeight: "calc(100% - 120px)" } }}
    >
      <DialogTitle>
        <div>Import CSV or Excel</div>
        <CloseButton onClose={onClose} />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ my: 1 }}>
          {!lastJobStatus.checked && (
            <Typography variant="caption">Preparing job...</Typography>
          )}
          {/* Always render FilePond but hide when file selected - prevents DOM conflict on unmount */}
          <Box
            sx={{
              display:
                !csvFile && lastJobStatus.checked && step !== "import"
                  ? "block"
                  : "none",
            }}
          >
            <Typography>
              Download
              <Button
                variant="text"
                sx={{ textTransform: "none", textDecoration: "underline" }}
                onClick={onSample}
              >
                a sample CSV template
              </Button>
              or check our
              <Button
                variant="text"
                sx={{ textTransform: "none", textDecoration: "underline" }}
                href={appFeatures.csv.helpUrl}
                target="_blank"
              >
                import guide
              </Button>
              to learn about the required format.
            </Typography>
            <FilePond
              ref={pondRef}
              name="file"
              allowMultiple={false}
              instantUpload={false}
              onupdatefiles={(fileItems) => {
                setCsvFile(fileItems[0]);
              }}
              acceptedFileTypes={ACCEPTED_TYPES}
              fileValidateTypeDetectType={detectImportFileType}
              fileValidateTypeLabelExpectedTypes="Expects a CSV or Excel (.xlsx, .xls) file"
              labelIdle='Drag & drop a CSV or Excel file or <span class="filepond--label-action">Browse</span>'
            />
            <Alert
              severity="info"
              sx={{ mt: 2 }}
              icon={<IconTypes.Concierge />}
            >
              Need help with file formatting or have custom data?{" "}
              <Link
                component={RouterLink}
                to="/import/concierge"
                onClick={onClose}
              >
                AI Concierge
              </Link>{" "}
              can guide you or import directly from documents, images, or
              descriptions.
            </Alert>
            <ImportStatusLine jobStatus={lastJobStatus?.status} />
          </Box>
          {csvFile && restored && !step && (
            <Alert severity="success" sx={{ mb: 1 }}>
              Your file is back. Pick up where you left off.
            </Alert>
          )}
          {csvFile && (
            <Box translate="no">
              File: <strong>{csvFile.filename}</strong>{" "}
              <small>
                ({Math.round((csvFile.fileSize / 1024) * 10) / 10} KB{" "}
                {result?.analysis?.recordsCount > 0 ? (
                  <span>
                    {" "}
                    - <b>{result.analysis.recordsCount}</b> records
                  </span>
                ) : null}
                )
              </small>
              {step !== "import" && (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ ml: 2 }}
                  onClick={resetImport}
                >
                  Replace file
                </Button>
              )}
              <HelpButton helpUrl={appFeatures.csv.helpUrl} sx={{ ml: 1 }} />
              {result?.sheetNote && (
                <Alert severity="warning" sx={{ my: 2 }}>
                  {result.sheetNote}
                </Alert>
              )}
              {result?.criticalStop && (
                <Box sx={{ my: 2 }}>
                  <Typography variant="subtitle2" color="error">
                    Import File Error
                  </Typography>

                  {result.errors?.map((e, i) => (
                    <Typography variant="body2" color="error" key={i}>
                      - {e.index ? e.index + ": " : ""}
                      <span dangerouslySetInnerHTML={{ __html: e.message }} />
                    </Typography>
                  ))}
                </Box>
              )}
              {!step && (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, my: 2, display: "flex", gap: 1 }}
                >
                  <CategorySelect
                    categoryId={options.categoryId}
                    onChange={(id) =>
                      setOptions({ ...options, categoryId: id })
                    }
                    defaultLabel="-- Select default category --"
                  />
                  <CollectionSelect
                    collectionId={options.collectionId}
                    onChange={(id) =>
                      setOptions({ ...options, collectionId: id })
                    }
                    defaultLabel="-- Import to collection (optional) --"
                  />
                </Paper>
              )}
            </Box>
          )}
          {step === "analyze" && (
            <StoreExportNotice
              result={result}
              options={options}
              setOptions={setOptions}
              onLeave={onClose}
            />
          )}
          {step === "analyze" && (
            <ImportCsvAnalysis
              options={options}
              setOptions={setOptions}
              result={result}
              setResult={setResult}
              stale={stale}
              returnUrl={CSV_RETURN_URL}
              onUpgradeClick={keepCsvForReturn}
            />
          )}
          {step === "analyze" &&
            (result?.analysis?.nameUnresolved || !!result?.sheetNote) && (
              <Alert
                severity="info"
                sx={{ mt: 2 }}
                icon={<IconTypes.Concierge />}
              >
                File not laid out as a simple table? You can fix the points
                above here for free, or let{" "}
                <Link
                  component={RouterLink}
                  to="/import/concierge"
                  onClick={onClose}
                >
                  AI Concierge
                </Link>{" "}
                read the file as it is (uses AI credits).
              </Alert>
            )}
          {step === "preview" && (
            <ClearedValuesNotice
              result={result}
              options={options}
              setOptions={setOptions}
            />
          )}
          {step === "preview" && (
            <ImportCsvPreview
              records={result.records}
              newCollections={chosenAnalysis?.analysis?.collections}
            />
          )}
          {(step === "import" || step === "done") && (
            <ImportProgress jobStatus={jobStatus} />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {notApproved > 0 && !uploading && (
          <Button
            color="warning"
            onClick={scrollToApprove}
            sx={{ mr: "auto", textTransform: "none" }}
          >
            {notApproved === 1
              ? "1 category with changes is not approved"
              : `${notApproved} categories with changes are not approved`}{" "}
            &darr;
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
        {csvFile && (
          <>
            {firstAnalysis &&
              !uploading &&
              (step === "preview" ||
                (step === "analyze" && result !== firstAnalysis)) && (
                <Button onClick={backToAnalysis}>&laquo; Back</Button>
              )}
            {!step && !uploading && (
              <ProcessButton
                onClick={() => process(true)}
                processing={uploading}
                label="Preview &raquo;"
                color="primary"
              />
            )}
            <ProcessButton
              onClick={() => process()}
              processing={uploading}
              label={
                step === "analyze"
                  ? stale
                    ? "Continue »"
                    : options.preview
                    ? "Preview »"
                    : "Update & Import"
                  : "Import"
              }
              variant={step ? "contained" : "outlined"}
              disabled={
                step === "done" || nameBlocked || categoryBlocked || storeBlocked
              }
            />
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
